import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./BrinkPaywall.module.scss"

/**
 * Brink — an EXPIRATION / win-back paywall (paywall type randomly rolled this
 * run — rolled *Expiration*). Built to the active Slack standing directive
 * ("build paywalls centered around loss aversion, use fear as the driving
 * factor. Include some amazing animations, highlight our feature list. Target
 * new users"): the fear is primal — *gravity*. A single memory, held in only
 * one place, is one dropped phone away from gone.
 *
 * Mechanic — a memory on the BRINK of a fall. The vault is a lit shelf that
 * holds your memories up and safe. Your free trial just ended, so on load the
 * shelf loses power: the front memory tips off the edge and *falls* into the
 * dark below, tumbling and draining of colour, coming to rest trembling just
 * shy of the void — real-world hazards ("lost phone", "cracked screen",
 * "factory reset") drift up through the dark. Status reads "Protection off · 1
 * copy". Nudge the scene and the memory lurches further toward the edge — you
 * feel how little is holding it. Press the CTA and the vault powers back on: a
 * cyan *catch beam* fires up from the vault mouth, arrests the fall and lifts
 * the memory home; every photo settles onto the re-lit shelf, a lock seals
 * across it, and the four Premium protections light one after another
 * ("Protected · backed up").
 *
 * Honest loss aversion. The void is the *real world* — a lost, broken or stolen
 * phone — never the app taking anything away. Explicit copy: photos are always
 * yours and are never deleted; protection just keeps a second, private copy so
 * a bad day for your phone is never a bad day for your memories. No countdown,
 * no fake scarcity, honest savings from the real prices.
 *
 * Divergence from the set: the signature is downward *gravity* + a light-beam
 * catch — not Lifeline's braided cord, Artifact's glitch-restore, Tideline's
 * tide, Homecoming's lift-the-cover wall, or Standby's console. Value frame is
 * single-copy fragility → a caught, backed-up copy; register is vertigo →
 * relief. Yearly is the featured, most-prominent price; monthly is one demoted
 * line. Self-contained on the built-in debug product data + bundled photos; no
 * native injection.
 */

// Real memories from the bundled set. `hero` is the one that slips off the
// shelf and falls; the rest stay on the shelf, teetering.
const HERO = "mem4.jpg"
const SHELF = ["mem9.jpg", "mem1.jpg", "mem12.jpg", "mem6.jpg"]

// Real-world hazards that drift up through the void — the honest source of the
// fear (never the app). Placed at fixed lanes so the scene reads calmly.
const HAZARDS = [
  { text: "lost phone", lane: 0.16, delay: 0 },
  { text: "cracked screen", lane: 0.52, delay: 2.1 },
  { text: "factory reset", lane: 0.8, delay: 3.4 },
  { text: "stolen", lane: 0.36, delay: 5.0 },
]

// The protections that switch back on with the vault — real Premium
// capabilities, lit one after another when sealed.
interface Guard {
  label: string
  detail: string
  icon: IconName
}
const GUARDS: Guard[] = [
  {
    label: "Private cloud backup",
    detail: "A second, encrypted copy",
    icon: "cloud",
  },
  {
    label: "Original quality",
    detail: "Full-resolution, never shrunk",
    icon: "image",
  },
  {
    label: "On every device",
    detail: "Synced where you sign in",
    icon: "devices",
  },
  {
    label: "Break-in alerts",
    detail: "Catches wrong-PIN attempts",
    icon: "alert",
  },
]

type IconName =
  | "lock"
  | "cloud"
  | "image"
  | "devices"
  | "alert"
  | "arrow"
  | "close"
  | "check"
  | "shield"
  | "bolt"

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

// Gravity fall: accelerate, then cushion to a trembling rest at the brink.
const easeFall = (t: number) =>
  t < 0.78
    ? Math.pow(t / 0.78, 1.9) * 0.94
    : 0.94 + easeOutCubic((t - 0.78) / 0.22) * 0.06

const Icon = ({ name }: { name: IconName }) => {
  switch (name) {
    case "lock":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="4.5"
            y="10.4"
            width="15"
            height="10.2"
            rx="3"
            fill="currentColor"
          />
          <path
            d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8"
            stroke="currentColor"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )
    case "cloud":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7 18h10a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6.5 9.2 3.9 3.9 0 0 0 7 18Z"
            fill="currentColor"
          />
          <path
            d="M12 15.6v-5m0 0 2 2m-2-2-2 2"
            stroke="var(--bk-ink)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "image":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="3.5"
            y="5"
            width="17"
            height="14"
            rx="3"
            fill="currentColor"
          />
          <circle cx="9" cy="10" r="1.7" fill="var(--bk-ink)" />
          <path
            d="m5 17 4-4 3.2 3 2.3-2.3L19 17.5"
            stroke="var(--bk-ink)"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "devices":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="2.5"
            y="5.5"
            width="13"
            height="10"
            rx="2"
            fill="currentColor"
          />
          <rect
            x="16"
            y="8.5"
            width="5.5"
            height="10"
            rx="1.6"
            fill="currentColor"
          />
          <path
            d="M6 19h6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )
    case "alert":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3.6 20.5 19a1 1 0 0 1-.87 1.5H4.37A1 1 0 0 1 3.5 19L12 3.6Z"
            fill="currentColor"
          />
          <path
            d="M12 9.5v4.2"
            stroke="var(--bk-ink)"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16.7" r="1.1" fill="var(--bk-ink)" />
        </svg>
      )
    case "shield":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2.6 20 5.4v6c0 4.7-3.2 8.4-8 10-4.8-1.6-8-5.3-8-10v-6L12 2.6Z"
            fill="currentColor"
          />
        </svg>
      )
    case "bolt":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" fill="currentColor" />
        </svg>
      )
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h13m0 0-5-5m5 5-5 5"
            stroke="currentColor"
            strokeWidth="2.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "close":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )
    case "check":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 12.5 4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
  }
}

const BrinkPaywall = () => {
  const { state, actions } = useAppState()

  const premium = state.productData?.premium
  const locale = premium?.yearlyProduct?.priceLocale

  const money = (v: number | undefined, fallback: string) =>
    v != null && locale ? formatCurrency(v, locale) : fallback

  const plan = useMemo(
    () => ({
      yearly: premium?.yearlyProduct?.price ?? 35.99,
      monthly: premium?.monthlyProduct?.price ?? 13.99,
      yearlyProduct: premium?.yearlyProduct,
      monthlyProduct: premium?.monthlyProduct,
    }),
    [premium],
  )

  const [billing, setBilling] = useState<"yearly" | "monthly">("yearly")

  // Two continuous scalars fully describe the scene:
  //   fall  0 → on the shelf … 1 → at the brink of the void
  //   seal  0 → vault off … 1 → powered, caught, sealed
  const [fall, setFall] = useState(0)
  const fallRef = useRef(0)
  const [seal, setSeal] = useState(0)
  const sealRef = useRef(0)
  const [tug, setTug] = useState(0)
  const tugRef = useRef(0)
  const [clock, setClock] = useState(0)
  const clockRef = useRef(0)
  const rafSeal = useRef(0)
  const rafFall = useRef(0)
  const rafClock = useRef(0)
  const reduced = useRef(false)

  const armed = seal > 0.5

  const animate = useCallback(
    (
      ref: React.MutableRefObject<number>,
      setter: (v: number) => void,
      rafSlot: React.MutableRefObject<number>,
      target: number,
      dur: number,
      ease: (t: number) => number,
    ) => {
      cancelAnimationFrame(rafSlot.current)
      if (reduced.current) {
        ref.current = target
        setter(target)
        return
      }
      const from = ref.current
      const start = performance.now()
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / dur)
        ref.current = lerp(from, target, ease(t))
        setter(ref.current)
        if (t < 1) rafSlot.current = requestAnimationFrame(tick)
      }
      rafSlot.current = requestAnimationFrame(tick)
    },
    [],
  )

  // Tint the OS top inset to the scene's dark ground; run edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#0b1120")
    actions.setIgnoreFooterInset(true)
  }, [actions])

  // Continuous clock: idle float, hazard drift, brink tremble; relax any tug.
  useEffect(() => {
    reduced.current =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true

    if (reduced.current) return
    const start = performance.now()
    const tick = (now: number) => {
      clockRef.current = (now - start) / 1000
      setClock(clockRef.current)
      if (tugRef.current > 0.001) {
        tugRef.current *= 0.9
        setTug(tugRef.current)
      }
      rafClock.current = requestAnimationFrame(tick)
    }
    rafClock.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafClock.current)
  }, [])

  // Auto-demo on load: hold on the lit shelf a beat (what the trial gave you),
  // then honestly cut the power so the front memory tips and falls to the brink.
  useEffect(() => {
    if (reduced.current) {
      fallRef.current = 1
      setFall(1)
      return
    }
    const t1 = setTimeout(
      () => animate(fallRef, setFall, rafFall, 1, 1350, (t) => t),
      680,
    )
    return () => {
      clearTimeout(t1)
      cancelAnimationFrame(rafFall.current)
      cancelAnimationFrame(rafSeal.current)
    }
  }, [animate])

  // Nudge the scene — feel how little holds a single copy.
  const onNudge = useCallback(() => {
    if (sealRef.current > 0.3 || fallRef.current < 0.5) return
    tugRef.current = 1
    setTug(1)
  }, [])

  const yearlyPrice = money(plan.yearly, "$35.99")
  const perMonthOfYear = money(plan.yearly / 12, "$2.99")
  const monthlyPrice = money(plan.monthly, "$13.99")
  const savePercent = Math.max(
    0,
    Math.round((1 - plan.yearly / (plan.monthly * 12)) * 100),
  )

  const selectedProduct =
    billing === "yearly" ? plan.yearlyProduct : plan.monthlyProduct

  const buy = useCallback(() => {
    // Power the vault: catch the fall and seal — then hand off to purchase.
    animate(sealRef, setSeal, rafSeal, 1, 1000, easeInOutCubic)
    if (selectedProduct) native.subscribe(selectedProduct)
  }, [animate, selectedProduct])

  const close = useCallback(() => native.postNativeEvent("close"), [])

  // ---- Derived visuals -----------------------------------------------------
  // exposure: how far the hero is into the void (fall, minus any catch by seal).
  const exposure = clamp01(
    fall * (1 - easeOutCubic(seal)) + tug * 0.14 * (1 - seal),
  )
  const ef = easeFall(exposure)
  const bob = exposure > 0.6 && seal < 0.2 ? Math.sin(clock * 3.1) * 2.2 : 0
  const sway = exposure > 0.6 && seal < 0.2 ? Math.sin(clock * 2.2) * 1.4 : 0
  const heroY = ef * 70 + bob
  const heroX = sway + tug * 6
  const heroRot =
    exposure * 15 + tug * 7 + (exposure > 0.6 ? Math.sin(clock * 2.6) * 1.6 : 0)
  const heroScale = lerp(1, 0.82, ef)
  const heroSat = lerp(1, 0.18, exposure)
  const heroBright = lerp(1, 0.62, exposure)
  const catchGlow = clamp01(seal * 2) * clamp01((1 - seal) * 2.4) // peaks mid-seal

  return (
    <div
      className={styles.page}
      data-armed={armed}
      style={
        {
          "--fall": exposure,
          "--seal": easeOutCubic(seal),
          "--catch": catchGlow,
        } as React.CSSProperties
      }
    >
      <div className={styles.aura} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Icon name="close" />
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <Icon name="lock" />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          One memory just slipped
          <br />
          off the <span className={styles.hl}>edge</span>.
        </h1>
        <p className={styles.sub}>
          Your free trial ended, so the vault powered down. Every photo is still
          here — but a memory kept in only one place is one dropped phone from
          gone.
        </p>
      </header>

      {/* Signature: the vault shelf, a memory falling, the void below. */}
      <section
        className={styles.stage}
        onPointerDown={onNudge}
        aria-label="A memory on the brink of falling from the vault"
      >
        <span className={styles.statusChip} data-armed={armed}>
          <span className={styles.statusDot} aria-hidden="true" />
          {armed ? "Protected · backed up" : "Protection off · 1 copy"}
        </span>

        {/* the lit shelf that holds memories up and safe */}
        <div className={styles.shelf}>
          <div className={styles.shelfRow}>
            {SHELF.map((f, i) => (
              <span className={styles.tile} data-i={i} key={f}>
                <img
                  src={`${process.env.PUBLIC_URL}/nimbus/${f}`}
                  alt=""
                  draggable={false}
                  loading="eager"
                />
              </span>
            ))}
            {/* the gap where the hero used to sit — fills back on catch */}
            <span className={styles.gap} aria-hidden="true" />
          </div>
          <span className={styles.shelfEdge} aria-hidden="true" />
          <span className={styles.lockBand} aria-hidden="true">
            <Icon name="lock" />
          </span>
        </div>

        {/* the void: real-world loss drifting up through the dark */}
        <div className={styles.void} aria-hidden="true">
          {HAZARDS.map((h) => (
            <span
              className={styles.hazard}
              key={h.text}
              style={
                {
                  left: `${h.lane * 100}%`,
                  animationDelay: `${h.delay}s`,
                } as React.CSSProperties
              }
            >
              {h.text}
            </span>
          ))}
          <span className={styles.floor} />
        </div>

        {/* the catch beam that fires from the vault mouth on seal */}
        <span className={styles.beam} aria-hidden="true" />

        {/* the falling memory */}
        <span
          className={styles.hero}
          style={{
            transform: `translate3d(${heroX}px, ${heroY}px, 0) rotate(${heroRot}deg) scale(${heroScale})`,
            filter: `saturate(${heroSat}) brightness(${heroBright})`,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/nimbus/${HERO}`}
            alt=""
            draggable={false}
            loading="eager"
          />
          <span className={styles.heroTag} data-armed={armed}>
            {armed ? "caught · 2nd copy" : "only copy"}
          </span>
        </span>
      </section>

      {/* the protections that switch back on with the vault */}
      <ul className={styles.guards}>
        {GUARDS.map((g, i) => (
          <li
            className={styles.guard}
            key={g.label}
            data-armed={armed}
            style={{ transitionDelay: armed ? `${120 + i * 90}ms` : "0ms" }}
          >
            <span className={styles.guardIcon}>
              <Icon name={g.icon} />
            </span>
            <span className={styles.guardText}>
              <strong>{g.label}</strong>
              <span>{g.detail}</span>
            </span>
            <span className={styles.guardMark} aria-hidden="true">
              <Icon name={armed ? "check" : "bolt"} />
            </span>
          </li>
        ))}
      </ul>

      {/* reassurance — the App-Store-compliance spine, said plainly */}
      <p className={styles.reassure}>
        <Icon name="shield" />
        Your photos are always yours and are never deleted — protection just
        keeps a safe second copy, so a lost or broken phone never means a lost
        memory.
      </p>

      {/* pricing — yearly featured; monthly demoted to a single line */}
      <section className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${billing === "yearly" ? styles.yearOn : ""}`}
          onClick={() => setBilling("yearly")}
          aria-pressed={billing === "yearly"}
        >
          <span className={styles.yearBadge}>
            BEST VALUE · SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>Premium · Yearly</span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>{perMonthOfYear}/mo, billed yearly</span>
            <span>Protection stays on all year</span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${billing === "monthly" ? styles.monthOn : ""}`}
          onClick={() => setBilling("monthly")}
          aria-pressed={billing === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <Icon name="bolt" />
          <span>Turn protection back on</span>
          <Icon name="arrow" />
        </button>

        <p className={styles.fine}>
          {billing === "yearly"
            ? `${yearlyPrice} per year`
            : `${monthlyPrice} per month`}
          . Protection resumes today · auto-renews until canceled · cancel
          anytime.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default BrinkPaywall
